import { useCallback, useEffect, useMemo, useState } from 'react';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useNavigation } from '@/context/NavigationContext';

const MAX_SEARCH_LENGTH = 200;
const DEFAULT_ALLOWED_PAGE_SIZES = [10, 25, 50, 100] as const;

export interface CommerceRegistryQueryState {
  search: string;
  pagination: PaginationState;
  sort: string[];
  registry: string | null;
  facets: Record<string, string[]>;
}

export interface CommerceRegistryQueryController extends CommerceRegistryQueryState {
  setSearch: (value: string) => void;
  onPaginationChange: OnChangeFn<PaginationState>;
  setSort: (values: string[]) => void;
  setRegistry: (value: string | null) => void;
  setFacet: (key: string, values: string[]) => void;
  clear: () => void;
}

function positiveInteger(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function splitPath(path: string | null | undefined): { pathname: string; params: URLSearchParams } {
  const source = path || (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');
  const queryIndex = source.indexOf('?');
  return {
    pathname: queryIndex >= 0 ? source.slice(0, queryIndex) : source,
    params: new URLSearchParams(queryIndex >= 0 ? source.slice(queryIndex + 1) : ''),
  };
}

export function withCommerceQueryValue(path: string, key: string, value: string | null): string {
  const { pathname, params } = splitPath(path);
  if (value?.trim()) params.set(key, value.trim());
  else params.delete(key);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function withCommerceRegistryQuery(targetPath: string, currentPath: string): string {
  const target = splitPath(targetPath);
  const current = splitPath(currentPath);
  const currentKeys = new Set<string>();
  current.params.forEach((_value, key) => currentKeys.add(key));
  currentKeys.forEach(key => {
    if (!target.params.has(key)) {
      current.params.getAll(key).forEach(value => target.params.append(key, value));
    }
  });
  const query = target.params.toString();
  return query ? `${target.pathname}?${query}` : target.pathname;
}

function replaceValues(params: URLSearchParams, key: string, values: string[]): void {
  params.delete(key);
  values.forEach(value => {
    const normalized = value.trim();
    if (normalized) params.append(key, normalized);
  });
}

export function useCommerceRegistryQuery({
  defaultPageSize = 10,
  facetKeys = ['status'],
  facetValues = {},
  sortFields = [],
  registryValues = [],
  allowedPageSizes = DEFAULT_ALLOWED_PAGE_SIZES,
}: {
  defaultPageSize?: number;
  facetKeys?: string[];
  facetValues?: Record<string, readonly string[]>;
  sortFields?: readonly string[];
  registryValues?: readonly string[];
  allowedPageSizes?: readonly number[];
} = {}): CommerceRegistryQueryController {
  const navigation = useNavigation();
  const facetKeySignature = facetKeys.join('\u0000');
  const facetValueSignature = facetKeys
    .map(key => `${key}:${(facetValues[key] ?? []).join('\u0001')}`)
    .join('\u0000');
  const sortFieldSignature = sortFields.join('\u0000');
  const registryValueSignature = registryValues.join('\u0000');
  const pageSizeSignature = allowedPageSizes.join('\u0000');
  const allowedPageSizeSet = useMemo(() => new Set(allowedPageSizes), [pageSizeSignature]);
  const allowedSortFieldSet = useMemo(() => new Set(sortFields), [sortFieldSignature]);
  const allowedRegistryValueSet = useMemo(() => new Set(registryValues), [registryValueSignature]);
  const allowedFacetValueSets = useMemo(
    () => Object.fromEntries(facetKeys.map(key => [key, new Set(facetValues[key] ?? [])])),
    [facetKeySignature, facetValueSignature],
  );
  const parsedState = useMemo<CommerceRegistryQueryState>(() => {
    const { params } = splitPath(navigation.path);
    const page = positiveInteger(params.get('page'), 1);
    const requestedPageSize = positiveInteger(params.get('page_size'), defaultPageSize);
    const pageSize = allowedPageSizeSet.has(requestedPageSize) ? requestedPageSize : defaultPageSize;
    const registry = params.get('registry')?.trim() || null;
    return {
      search: (params.get('q') ?? '').trim().slice(0, MAX_SEARCH_LENGTH),
      pagination: { pageIndex: page - 1, pageSize },
      sort: params.getAll('sort').map(value => value.trim()).filter(value => {
        const field = value.startsWith('-') ? value.slice(1) : value;
        return field.length > 0 && allowedSortFieldSet.has(field);
      }),
      registry: registry && allowedRegistryValueSet.has(registry) ? registry : null,
      facets: Object.fromEntries(
        facetKeys.map(key => [
          key,
          Array.from(new Set(params.getAll(key)
            .map(value => value.trim())
            .filter(value => allowedFacetValueSets[key]?.has(value))))
            .sort(),
        ]),
      ),
    };
  }, [
    allowedFacetValueSets,
    allowedPageSizeSet,
    allowedRegistryValueSet,
    allowedSortFieldSet,
    defaultPageSize,
    facetKeySignature,
    navigation.path,
  ]);
  const [state, setState] = useState(parsedState);

  useEffect(() => setState(parsedState), [parsedState]);

  const update = useCallback((mutate: (params: URLSearchParams) => void, replace = false) => {
    const { pathname, params } = splitPath(navigation.path);
    mutate(params);
    const query = params.toString();
    navigation.navigate(query ? `${pathname}?${query}` : pathname, { replace });
  }, [navigation]);

  const setSearch = useCallback((value: string) => {
    const normalized = value.trim().slice(0, MAX_SEARCH_LENGTH);
    setState(current => ({
      ...current,
      search: normalized,
      pagination: { ...current.pagination, pageIndex: 0 },
    }));
    update(params => {
      if (normalized) params.set('q', normalized);
      else params.delete('q');
      params.delete('page');
    }, true);
  }, [update]);

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>((updaterOrValue) => {
    const requested = typeof updaterOrValue === 'function' ? updaterOrValue(state.pagination) : updaterOrValue;
    const pageSize = allowedPageSizeSet.has(requested.pageSize) ? requested.pageSize : defaultPageSize;
    const next = {
      pageIndex: pageSize === state.pagination.pageSize ? Math.max(0, requested.pageIndex) : 0,
      pageSize,
    };
    setState(current => ({ ...current, pagination: next }));
    update(params => {
      if (next.pageIndex > 0) params.set('page', String(next.pageIndex + 1));
      else params.delete('page');
      if (next.pageSize !== defaultPageSize) params.set('page_size', String(next.pageSize));
      else params.delete('page_size');
    });
  }, [allowedPageSizeSet, defaultPageSize, state.pagination, update]);

  const setFacet = useCallback((key: string, values: string[]) => {
    if (!facetKeys.includes(key)) return;
    const normalizedValues = Array.from(new Set(values
      .map(value => value.trim())
      .filter(value => allowedFacetValueSets[key]?.has(value))))
      .sort();
    setState(current => ({
      ...current,
      facets: { ...current.facets, [key]: normalizedValues },
      pagination: { ...current.pagination, pageIndex: 0 },
    }));
    update(params => {
      replaceValues(params, key, normalizedValues);
      params.delete('page');
    });
  }, [allowedFacetValueSets, facetKeySignature, update]);

  const setSort = useCallback((values: string[]) => {
    const normalizedValues = values.map(value => value.trim()).filter(value => {
      const field = value.startsWith('-') ? value.slice(1) : value;
      return field.length > 0 && allowedSortFieldSet.has(field);
    });
    setState(current => ({
      ...current,
      sort: normalizedValues,
      pagination: { ...current.pagination, pageIndex: 0 },
    }));
    update(params => {
      replaceValues(params, 'sort', normalizedValues);
      params.delete('page');
    });
  }, [allowedSortFieldSet, update]);

  const setRegistry = useCallback((value: string | null) => {
    const requestedValue = value?.trim() || null;
    const normalizedValue = requestedValue && allowedRegistryValueSet.has(requestedValue)
      ? requestedValue
      : null;
    setState(current => ({
      ...current,
      registry: normalizedValue,
      pagination: { ...current.pagination, pageIndex: 0 },
    }));
    update(params => {
      if (normalizedValue) params.set('registry', normalizedValue);
      else params.delete('registry');
      params.delete('page');
    });
  }, [allowedRegistryValueSet, update]);

  const clear = useCallback(() => {
    setState(current => ({
      ...current,
      search: '',
      sort: [],
      facets: Object.fromEntries(facetKeys.map(key => [key, []])),
      pagination: { pageIndex: 0, pageSize: defaultPageSize },
    }));
    update(params => {
      params.delete('q');
      params.delete('page');
      params.delete('page_size');
      params.delete('sort');
      facetKeys.forEach(key => params.delete(key));
    });
  }, [defaultPageSize, facetKeySignature, update]);

  return { ...state, setSearch, onPaginationChange, setSort, setRegistry, setFacet, clear };
}
